import { CardShell, EmptyState, LinkAction, SectionCard } from '@atlas/ui-components'
import { Button, buttonVariants, cn, Input, Skeleton, Tag, Textarea } from '@atlas/ui-primitives'
import { ArrowRight, ArrowUpRight, ListChecks } from 'lucide-react'
import { useState } from 'react'

/**
 * Severity drives sort order only - blocking rules lead, so no host has to re-sort its
 * own list. Kept as a string union rather than imported from the domain packages, so
 * this card stays free of them - the host narrows its own enum onto it.
 */
export type RuleListSeverity = 'blocking' | 'warning'

/**
 * Each rule is its own card on the recessed surface, so a row reads as one object rather
 * than as a stripe between two hairlines - and the white fields inside an edit form stay
 * legible against it.
 *
 * Shared by a real row and its skeleton, so the placeholder rows sit on the same rhythm.
 */
const ROW_CLASS = 'bg-ds-surface p-4'

/** Gap-separated rather than divided: the space between cards is the separator. */
const LIST_CLASS = 'space-y-3'

const fieldLabel = 'mb-1.5 block font-mono text-ds-3xs uppercase tracking-wide text-ds-text-subtle'

/** The card's default heading. Shared with a host's own skeleton so the two cannot diverge. */
export const APPLICABLE_RULES_TITLE = 'Applicable rules'

/** One rule as the card states it: its title, its category, and what it checks for. */
export interface RuleListItem {
	id: string
	statement: string
	severity: RuleListSeverity
	/** The rule's category (Brand/Channel/Regional/Compliance), shown under the title. */
	type: string
	/** The executable instruction handed to the generation step. */
	checkDefinition: string
}

/** The editable subset - the fields that carry a rule into the generation step. */
export interface RuleListPatch {
	statement?: string
	checkDefinition?: string
}

/**
 * The card's body while the coverage query runs: three row cards on the same rhythm a real
 * one sits on, each a type line over a title line and a details line.
 *
 * Exported so a host's own first-read skeleton stands the card in the same way, rather than
 * as generic text lines that then reflow into rows.
 */
export function ApplicableRulesBodySkeleton({ label }: { label?: string }) {
	return (
		<>
			{label ? <span className="sr-only">{label}</span> : null}
			<ul aria-busy="true" className={LIST_CLASS}>
				{Array.from({ length: 3 }, (_, i) => (
					<CardShell as="li" key={i} className={ROW_CLASS}>
						<div className="min-w-0 flex-1 space-y-2">
							<Skeleton shape="text" className="h-3 w-1/4" />
							<Skeleton shape="text" className="w-full" />
							<Skeleton shape="text" className="h-3 w-2/3" />
						</div>
					</CardShell>
				))}
			</ul>
		</>
	)
}

/**
 * Free-text editor that buffers locally and commits on blur.
 *
 * The host persists remotely, so the typed text only returns through props once the
 * mutation settles and its query refetches - and the mutation's own state changes
 * re-render this card in the meantime. Binding the field straight to the prop would
 * snap it back to the last-saved value mid-typing, dropping every character after the
 * first. So the draft lives here, and only a finished edit is reported upward.
 *
 * A value genuinely changed elsewhere (someone else's edit, arriving on refetch) still
 * wins: the caller keys the field on the value it shows, so a new one remounts it and
 * discards a stale draft. That is only safe because the field is blur-committed - an
 * uncommitted draft is one the user has not finished, and a committed one is already
 * reflected in the prop.
 */
function TitleEditor({
	rule,
	onEditRule,
}: {
	rule: RuleListItem
	onEditRule: (ruleId: string, patch: RuleListPatch) => void
}) {
	const [draft, setDraft] = useState(rule.statement)

	const commit = () => {
		if (draft !== rule.statement) onEditRule(rule.id, { statement: draft })
	}

	return (
		<div>
			<label className={fieldLabel} htmlFor={`rule-title-${rule.id}`}>
				Rule title
			</label>
			<Input
				id={`rule-title-${rule.id}`}
				size="sm"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				className="font-medium"
			/>
		</div>
	)
}

/** Same commit-on-blur behaviour as `TitleEditor`, for the rule's check definition. */
function CheckDefinitionEditor({
	rule,
	onEditRule,
}: {
	rule: RuleListItem
	onEditRule: (ruleId: string, patch: RuleListPatch) => void
}) {
	const [draft, setDraft] = useState(rule.checkDefinition)

	const commit = () => {
		if (draft !== rule.checkDefinition) onEditRule(rule.id, { checkDefinition: draft })
	}

	return (
		<div>
			<label className={fieldLabel} htmlFor={`rule-check-${rule.id}`}>
				Check definition
			</label>
			<Textarea
				id={`rule-check-${rule.id}`}
				size="sm"
				rows={2}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				className="leading-5"
			/>
		</div>
	)
}

/**
 * One rule as its own card: the type as an eyebrow tag, then the title, then what it
 * checks for. The order is the same whether the fields are read or edited, so the list
 * does not re-flow when a host grants edit permission.
 *
 * `href` sends the reader to the rule's own page. In-place editing covers the two fields
 * that feed generation; scope, severity and element types live only there.
 */
function RuleRow({
	rule,
	onEditRule,
	href,
	onOpen,
	onValidate,
	isValidating,
}: {
	rule: RuleListItem
	onEditRule?: (ruleId: string, patch: RuleListPatch) => void
	href?: string
	onOpen?: () => void
	onValidate?: () => void
	/** Whether the request in flight covers THIS rule. */
	isValidating?: boolean
}) {
	return (
		<CardShell as="li" className={ROW_CLASS}>
			<div className="space-y-3">
				{/* Category left, the way out right: both are labels about the rule rather than the
				    rule itself, so they share one rail and the same mono register. */}
				<div className="flex items-center justify-between gap-2">
					<Tag variant="muted" size="sm" className="font-mono text-ds-2xs uppercase tracking-wide">
						{rule.type}
					</Tag>
					{href ? (
						<LinkAction
							href={href}
							onNavigate={onOpen}
							aria-label={`Edit rule: ${rule.statement}`}
							className="inline-flex shrink-0 items-center gap-1 font-mono text-ds-2xs uppercase tracking-wide"
						>
							Edit rule
							<ArrowUpRight aria-hidden className="h-3.5 w-3.5" />
						</LinkAction>
					) : null}
				</div>
				{onEditRule ? (
					<TitleEditor key={`title-${rule.statement}`} rule={rule} onEditRule={onEditRule} />
				) : (
					<p className="text-ds-sm font-semibold leading-5 text-ds-text">{rule.statement}</p>
				)}
				{onEditRule ? (
					<CheckDefinitionEditor key={`check-${rule.checkDefinition}`} rule={rule} onEditRule={onEditRule} />
				) : (
					<div>
						<span className={fieldLabel}>Check definition</span>
						<p className="text-ds-sm leading-5 text-ds-text-muted">
							{rule.checkDefinition || 'No check definition yet.'}
						</p>
					</div>
				)}
				{onValidate ? (
					<Button
						size="lg"
						variant="primary"
						isDisabled={isValidating}
						onPress={onValidate}
						aria-label={`Validate rule: ${rule.statement}`}
						className="w-full"
					>
						{isValidating ? 'Validating…' : 'Validate'}
					</Button>
				) : null}
			</div>
		</CardShell>
	)
}

export interface ApplicableRulesCardProps {
	rules: RuleListItem[]
	/** Overrides the section heading, for a surface that names the section differently. */
	title?: string
	/** The rules-explorer destination for the heading action. Omit it and no action renders. */
	viewAllHref?: string
	/** Called instead of navigating, so the host performs in-app navigation. */
	onViewAll?: () => void
	/**
	 * The rules-manager destination for one rule, by id. Omit it and rows carry no link.
	 *
	 * A function rather than a field on `RuleListItem`, so the host owns the URL shape and
	 * every adapter feeding this card stays a pure projection of the rule itself.
	 */
	ruleHref?: (ruleId: string) => string
	/** Called instead of navigating to `ruleHref`, so the host performs in-app navigation. */
	onOpenRule?: (ruleId: string) => void
	isLoading?: boolean
	/**
	 * Whether the coverage read failed. Distinct from an empty result: "no rules apply" is an
	 * answer a reader acts on, and reporting it for a read that never landed would clear a
	 * context of rules the service was simply unable to state.
	 */
	isError?: boolean
	/** What an empty result means here. */
	emptyLabel?: string
	/**
	 * Whether to render edit affordances. The card is TOLD, never asks: the routed
	 * container owns the permission decision, and the API is the real authorization
	 * guard - a read-only card is a UX affordance, not security.
	 */
	canEdit?: boolean
	/**
	 * Called with only the changed fields. Required for editing to do anything.
	 *
	 * Both fields report on blur, so a host that persists remotely receives one
	 * finished edit rather than one per keystroke. Safe to wire straight to a mutation.
	 */
	onEditRule?: (ruleId: string, patch: RuleListPatch) => void
	/**
	 * Shown under the heading. Use it to state a surprising scope - e.g. that edits
	 * apply to the rule everywhere, not just the surface being viewed.
	 */
	note?: string
	/**
	 * Where this context's recorded validation results are listed. Omit it and no link
	 * renders - a surface with no validation target has none to point at.
	 */
	resultsHref?: string
	/** Called instead of navigating to `resultsHref`, so the host performs in-app navigation. */
	onViewResults?: () => void
	/**
	 * Request a validation run covering this rule alone. Omit it and rows carry no
	 * validate control, which is the right answer wherever no target can be validated -
	 * a rule in the explorer is not attached to anything to check it against.
	 */
	onValidateRule?: (ruleId: string) => void
	/** Rules the request in flight covers. Their controls read `Validating…` and refuse a second press. */
	validatingRuleIds?: string[]
	/**
	 * Request a validation run covering every rule listed. Omit it and the heading carries
	 * no validate-all control, on the same terms as `onValidateRule`.
	 */
	onValidateAll?: () => void
	/** Whether the request in flight is the all-rules one. */
	isValidatingAll?: boolean
}

/**
 * Every rule covering the context the host resolved, at any severity.
 *
 * The card does NOT filter by severity: it is the inventory of everything that applies.
 * Severity still orders the list (blocking leads), but is otherwise the rule's own
 * business - a new rule defaults to blocking severity and multimodal-LLM validation
 * wherever it is created, so this card has nothing further to say about either.
 *
 * Carries its own loading and failure states, because a coverage query is typically a
 * separate request from the page around it: a slow one must not blank the page and a
 * failed one must not pass for a context nothing applies to.
 *
 * Where the host both permits editing and supplies somewhere to send the edit, a rule's
 * title and check definition are editable in place. This is the app's ONE resolved-rule
 * rendering: a surface that shows rules shows them here, so a reader meets the same row
 * wherever they meet a rule.
 *
 * States nothing about whether content SATISFIES these rules: a verdict belongs to a run,
 * and a run's verdicts are read where the host points `resultsHref`. What the card offers
 * is the way INTO one - a per-rule validate control where the host has a target to check
 * against - so a reader asks for the check here and reads the answer where it is recorded.
 */
export function ApplicableRulesCard({
	rules,
	title = APPLICABLE_RULES_TITLE,
	viewAllHref,
	onViewAll,
	ruleHref,
	onOpenRule,
	isLoading,
	isError,
	emptyLabel = 'No rules apply to this context.',
	canEdit = false,
	onEditRule,
	note,
	resultsHref,
	onViewResults,
	onValidateRule,
	validatingRuleIds,
	onValidateAll,
	isValidatingAll,
}: ApplicableRulesCardProps) {
	// Editing needs both permission and a sink for the changes; without a callback an
	// edit affordance would silently discard input.
	const editable = canEdit && !!onEditRule
	// Ordered here rather than by each host: what would stop a deliverable leads, then
	// alphabetically so the list never reshuffles between reads.
	const ordered = [...rules].sort(
		(a, b) =>
			(a.severity === 'blocking' ? 0 : 1) - (b.severity === 'blocking' ? 0 : 1) ||
			a.statement.localeCompare(b.statement),
	)

	return (
		<SectionCard
			title={title}
			count={isLoading || isError ? undefined : ordered.length}
			description={note}
			action={
				onValidateAll || resultsHref || viewAllHref ? (
					<span className="flex items-center gap-3">
						{onValidateAll ? (
							<Button size="sm" variant="outline" isDisabled={isValidatingAll} onPress={onValidateAll}>
								{isValidatingAll ? 'Requesting…' : 'Validate all'}
							</Button>
						) : null}
						{resultsHref ? (
							<LinkAction
								href={resultsHref}
								onNavigate={onViewResults}
								className={cn(
									buttonVariants({ variant: 'outline', size: 'sm' }),
									'gap-1.5 no-underline hover:no-underline',
								)}
							>
								<ListChecks aria-hidden className="h-4 w-4" /> Validation results
							</LinkAction>
						) : null}
						{viewAllHref ? (
							<LinkAction href={viewAllHref} onNavigate={onViewAll} aria-label="All applicable rules">
								<ArrowRight aria-hidden className="h-4 w-4" />
							</LinkAction>
						) : null}
					</span>
				) : null
			}
		>
			{isLoading ? (
				<ApplicableRulesBodySkeleton label="Resolving applicable rules…" />
			) : isError ? (
				<p className="text-ds-sm leading-5 text-ds-text-muted">
					Could not resolve the rules for this context. Please retry.
				</p>
			) : ordered.length === 0 ? (
				<EmptyState title={emptyLabel} />
			) : (
				<ul className={LIST_CLASS}>
					{ordered.map((rule) => (
						<RuleRow
							key={rule.id}
							rule={rule}
							onEditRule={editable ? onEditRule : undefined}
							href={ruleHref?.(rule.id)}
							onOpen={onOpenRule ? () => onOpenRule(rule.id) : undefined}
							onValidate={onValidateRule ? () => onValidateRule(rule.id) : undefined}
							isValidating={validatingRuleIds?.includes(rule.id)}
						/>
					))}
				</ul>
			)}
		</SectionCard>
	)
}
